import { Role } from '@/lib/role'
import { unique } from '@/lib/utils/unique'
import { BETA, PROD, ServerEnv, TRAINING } from '@/lib/server-env'

export enum Permission {
  // environment access
  ACCESS_PROD = 'access_prod',
  ACCESS_BETA = 'access_beta',
  ACCESS_DEV = 'access_dev',
  ACCESS_NONPROD = 'access_nonprod',
  ACCESS_TRAINING = 'access_training',

  // api permissions
  READ_ALL = 'read_all',
  READ_LIMITED = 'read_limited', // sets obfuscate = true
  UPDATE = 'update',
  DELETE = 'delete',

  // ui view permissions
  FULL_VIEW = 'full_view',
  VIEW_LIMITED = 'view_limited',
  VIEW_RESTRICTED = 'view_restricted',
}

export const viewRestrictedPermissions = [Permission.READ_LIMITED, Permission.VIEW_RESTRICTED]

export const fullAccessPermissions = [
  Permission.READ_ALL,
  Permission.DELETE,
  Permission.UPDATE,
  Permission.FULL_VIEW,
]

export const viewOnlyPermissions = [Permission.READ_ALL, Permission.VIEW_LIMITED]

export function mapRoleToEnvAccess(role: Role): Permission[] {
  switch (role) {
    case Role.PROD_USER:
      return [Permission.ACCESS_PROD]
    case Role.BETA_USER:
      return [Permission.ACCESS_BETA]
    case Role.NONPROD_USER:
      return [Permission.ACCESS_NONPROD]
    case Role.TRAINING_USER:
      return [Permission.ACCESS_TRAINING]
    case Role.DEVELOPER:
      return [Permission.ACCESS_DEV]
    default:
      return []
  }
}

export function mapRoleToPermissions(
  role: Role,
  atHomeCity: boolean | undefined = undefined,
  currentEnv: ServerEnv | undefined = undefined,
  override: boolean = true
): Permission[] {
  switch (role) {
    case Role.DEVELOPER:
      return currentEnv === PROD || currentEnv === BETA || !currentEnv
        ? [Permission.READ_ALL, Permission.FULL_VIEW]
        : fullAccessPermissions
    case Role.TEMPORARY_DUTY_AGENT:
      return fullAccessPermissions
    case Role.STATION_AGENT:
      if (override) {
        return fullAccessPermissions
      }
      return atHomeCity ? fullAccessPermissions : viewOnlyPermissions
    case Role.TRAINING_USER:
      return currentEnv === TRAINING ? fullAccessPermissions : []
    default:
      return []
  }
}

export function getPermissions(
  roles: Role[] | undefined,
  atHomeCity: boolean = true,
  serverEnv: ServerEnv | undefined = undefined,
  override: boolean = true
): Permission[] {
  if (!roles || roles.length === 0) {
    return [...viewRestrictedPermissions]
  }

  const envAccessPermissions = roles.flatMap((role) => mapRoleToEnvAccess(role))
  let appPermissions = roles.flatMap((role) =>
    mapRoleToPermissions(role, atHomeCity, serverEnv, override)
  )

  if (appPermissions.length === 0) {
    appPermissions = viewRestrictedPermissions
  }

  return unique([...envAccessPermissions, ...appPermissions])
}

export function hasPermission(
  roles: Role[] | undefined,
  permission: Permission,
  atHomeCity: boolean = true,
  serverEnv: ServerEnv | undefined = undefined
): boolean {
  const permissions = getPermissions(roles, atHomeCity, serverEnv)
  return permissions.includes(permission)
}
